import { memo, useCallback } from 'react';

import Divider from '@zenbusiness/zui/Divider';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import AuthorizationTransactionRow from '#client/components/AuthorizationTransactionRow';
import CheckDepositNeedReviewRow from '#client/components/CheckDepositHistory/components/CheckDepositNeedReviewRow';
import CheckDepositRow from '#client/components/CheckDepositHistory/components/CheckDepositRow';
import PaymentRow from '#client/components/PaymentContainer/components/PaymentRow';
import { NEEDS_REVIEW_STATUSES } from '#client/constants/transaction';

const PENDING_ITEM_TYPE = {
  PAYMENT: 'Payment',
  CHECK_DEPOSIT: 'CheckDeposit',
  AUTHORIZATION: 'Authorization'
};

const ListPendingSection = ({ data, refetchPayment = null }) => {
  const renderPaymentRow = useCallback(
    (row) => {
      const {
        id,
        type,
        amount,
        summary,
        toAccount,
        createdAt,
        fromAccount,
        transactionId,
        paymentStatus,
        paymentDirection,
        estimatedCompletionDate,
        reason,
        sameDay,
        description,
        from,
        to,
        counterpartyId
      } = row || {};
      return (
        <PaymentRow
          amount={amount.formattedAmount}
          counterpartyIdFromTransaction={counterpartyId}
          createdAt={createdAt}
          description={description}
          direction={paymentDirection}
          estimatedCompletionDate={estimatedCompletionDate}
          from={from}
          fromAccount={fromAccount}
          id={id}
          key={id}
          reason={reason}
          refetchPayment={refetchPayment}
          sameDay={sameDay}
          status={paymentStatus}
          summary={summary}
          to={to}
          toAccount={toAccount}
          transactionId={transactionId}
          type={type}
        />
      );
    },
    [refetchPayment]
  );

  const renderCheckDepositRow = useCallback((row) => {
    const {
      id,
      type,
      reason,
      amount,
      balance,
      summary,
      createdAt,
      transactionId,
      checkDepositStatus,
      estimatedCompletionDate
    } = row || {};
    if (NEEDS_REVIEW_STATUSES.includes(checkDepositStatus)) {
      return (
        <CheckDepositNeedReviewRow
          createdAt={createdAt}
          id={id}
          key={id}
          summary={summary}
        />
      );
    }

    return (
      <CheckDepositRow
        amount={amount.formattedAmount}
        balance={balance}
        createdAt={createdAt}
        estimatedCompletionDate={estimatedCompletionDate}
        id={id}
        key={id}
        reason={reason}
        status={checkDepositStatus}
        summary={summary}
        transactionId={transactionId}
        type={type}
      />
    );
  }, []);

  const renderAuthorizationRow = useCallback((row) => {
    const {
      id,
      type,
      amount,
      summary,
      recurring,
      createdAt,
      paymentMethod,
      authorizationDirection,
      authorizationStatus,
      cardId,
      declineReason,
      digitalWallet,
      cardPresent,
      verificationMethod,
      merchantName,
      merchantLocation,
      merchantCategory,
      currencyConversion
    } = row || {};
    return (
      <AuthorizationTransactionRow
        amount={amount.formattedAmount}
        cardId={cardId}
        cardPresent={cardPresent}
        createdAt={createdAt}
        // FX Details
        currencyConversion={currencyConversion}
        digitalWallet={digitalWallet}
        direction={authorizationDirection}
        id={id}
        key={id}
        merchantCategory={merchantCategory}
        merchantLocation={merchantLocation}
        merchantName={merchantName}
        // Merchant details
        paymentMethod={paymentMethod}
        reason={declineReason}
        recurring={recurring}
        status={authorizationStatus}
        summary={summary}
        type={type}
        verificationMethod={verificationMethod}
      />
    );
  }, []);

  const renderRowsByType = useCallback(
    (row) => {
      // eslint-disable-next-line no-underscore-dangle
      switch (row?.__typename) {
        case PENDING_ITEM_TYPE.PAYMENT:
          return renderPaymentRow(row);
        case PENDING_ITEM_TYPE.CHECK_DEPOSIT:
          return renderCheckDepositRow(row);
        case PENDING_ITEM_TYPE.AUTHORIZATION:
          return renderAuthorizationRow(row);
        default:
          return null;
      }
    },
    [renderPaymentRow, renderCheckDepositRow, renderAuthorizationRow]
  );

  return (
    <Stack
      aria-label="List of payments"
      direction="column"
      paddingBottom="20px"
      role="list"
    >
      <Stack
        direction="column"
        spacing="8px"
      >
        <Text
          color="secondary"
          size="xs"
          weight="medium"
        >
          PENDING
        </Text>
        <Divider />
        <Stack
          direction="column"
          spacing="8px"
        >
          {(data || []).map((row) => renderRowsByType(row))}
        </Stack>
      </Stack>
    </Stack>
  );
};

ListPendingSection.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  refetchPayment: PropTypes.func
};

const areEqual = (preProp, nextProp) =>
  preProp.data === nextProp.data && preProp.refetchPayment === nextProp.refetchPayment;

export default memo(ListPendingSection, areEqual);
